import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  FiPhoneCall, FiPhoneIncoming, FiPhoneOutgoing, FiRotateCcw, FiCheckSquare, FiList,
  FiSearch, FiX, FiFileText, FiDownload, FiUserPlus, FiCheck, FiClock, FiAlertCircle, FiUser,
} from 'react-icons/fi';
import { dashboardAPI, callsAPI, tasksAPI, dispositionsAPI } from '../../services/api';
import { formatDuration, formatClock, formatDmy, formatDateTime, getStatusLabel } from '../../utils/formatters';
import { usePhoneCore, isInternalNumber } from '../../phone/PhoneContext';
import CallNoteModal from '../../components/calls/CallNoteModal';
import OutcomeSelect from '../../components/calls/OutcomeSelect';
import RatingStars from '../../components/RatingStars';
import PeerCard from './PeerCard';

const PERIODS = [
  { key: 'today', label: 'Bugun', days: 0 },
  { key: '7', label: '7 kun', days: 6 },
  { key: '30', label: '30 kun', days: 29 },
];

// MAHALLIY sana (YYYY-MM-DD). `toISOString()` UTC ga o'giradi va server UTC+5
// bo'lgani uchun soat 00:00–04:59 oralig'ida KECHAGI sanani berardi.
const localDate = (d = new Date()) => {
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
};

const rangeFor = (key) => {
  const days = (PERIODS.find((x) => x.key === key) || PERIODS[1]).days;
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { start_date: localDate(start), end_date: localDate() };
};

const callNumber = (c) => (c.direction === 'inbound' ? c.caller_number : c.called_number);

// Bir vaqtda ekranga chiqariladigan qatorlar. 200 qatorni birdaniga chizish
// kuchsiz kompyuterda sezilarli qotishga olib kelardi.
const PAGE = 50;

// Yozuvning JSON ko'rinishi — har so'rovda eski obyektlarni qayta satrga
// aylantirmaslik uchun keshlanadi.
const jsonCache = new WeakMap();
const asJson = (o) => {
  let j = jsonCache.get(o);
  if (j === undefined) { j = JSON.stringify(o); jsonCache.set(o, j); }
  return j;
};

// Har 15 soniyada kelgan ro'yxatda O'ZGARMAGAN yozuvlar uchun eski obyekt
// qaytariladi. Natijada: hech narsa o'zgarmasa — React umuman qayta chizmaydi;
// bitta yangi qo'ng'iroq kelsa — faqat o'sha qator chiziladi, qolganlari emas.
function reuseRows(prev, next) {
  const byId = new Map(prev.map((x) => [x.id, x]));
  let same = prev.length === next.length;
  const out = next.map((n, i) => {
    const o = byId.get(n.id);
    if (o && asJson(o) === JSON.stringify(n)) {
      if (prev[i] !== o) same = false;
      return o;
    }
    same = false;
    return n;
  });
  return same ? prev : out;
}

// Vazifa sarlavhasidan raqamni ajratib olish (kontakt biriktirilmagan bo'lsa)
function taskNumber(t) {
  if (t.contact_phone) return t.contact_phone;
  const m = (t.title || '').match(/(\+?\d[\d\s-]{5,}\d)/);
  return m ? m[1].replace(/[^\d+]/g, '') : null;
}

function Kpi({ label, value, sub, color }) {
  return (
    <div className="op2-kpi g">
      <div className="op2-kpi-l">{label}</div>
      <div className="op2-kpi-v" style={color ? { color } : undefined}>{value}</div>
      {sub ? <div className="op2-kpi-s">{sub}</div> : null}
    </div>
  );
}

// Bitta qo'ng'iroq qatori — ustunlarga ajratilgan.
//   Mijoz ustuni  → bosilsa TEPADA mijoz kartasi ochiladi
//   Raqam ustuni  → bosilsa darhol qo'ng'iroq qilinadi
// memo: qator faqat o'z yozuvi yoki funksiyalari o'zgarganda qayta chiziladi.
const CallRow = memo(function CallRow({ call, codes, onOpenCard, onDial, onNote, onAddContact, onChanged }) {
  const num = callNumber(call);
  const answered = call.status === 'answered';
  const internal = isInternalNumber(num);
  return (
    <div className={`op2-call${answered ? '' : ' bad'}`}>
      {call.direction === 'inbound'
        ? <FiPhoneIncoming size={16} color="var(--g-ac)" />
        : <FiPhoneOutgoing size={16} color="var(--g-warn)" />}

      <button className="op2-call-name" onClick={() => onOpenCard(num)} title="Mijoz kartasini ochish">
        <span className={`nm${call.contact_name ? '' : ' none'}`}>
          {call.contact_name || (internal ? `Ichki ${num}` : 'Nomsiz raqam')}
        </span>
        <span className="row">
          {!answered && <span className="op2-tag bad">{getStatusLabel(call.status)}</span>}
          <RatingStars value={call.rating} />
        </span>
      </button>

      <button className="op2-call-num" onClick={() => onDial(num, call.contact_name)} title="Qo'ng'iroq qilish">
        <span className="nu"><FiPhoneCall size={13} /> {num}</span>
      </button>

      <div className="op2-call-time">
        <b>{formatClock(call.call_date)}</b>
        {formatDmy(call.call_date)}
        {answered ? ` · ${formatDuration(call.billable_sec || call.duration)}` : ''}
      </div>

      <div className="op2-call-acts">
        <OutcomeSelect call={call} codes={codes} onChanged={onChanged} />
        {!call.contact_name && num && !internal ? (
          <button className="op2-sm" title="Kontaktga qo'shish" onClick={() => onAddContact(num)}>
            <FiUserPlus size={15} />
          </button>
        ) : <span />}
        <button
          className={`op2-sm${call.notes ? ' on' : ''}`}
          title={call.notes ? "Izohni ko'rish" : "Izoh qo'shish"}
          onClick={() => onNote(call)}
        >
          <FiFileText size={15} />
        </button>
        {call.has_recording && answered ? (
          <a
            className="op2-sm"
            href={callsAPI.downloadRecording(call.id)}
            target="_blank"
            rel="noreferrer"
            title="Yozuvni yuklab olish"
          >
            <FiDownload size={15} />
          </a>
        ) : <span />}
      </div>

      {call.notes && (
        <div className="op2-call-note">
          <FiFileText size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{call.notes}</span>
        </div>
      )}
    </div>
  );
});

// Ustun sarlavhalari (keng ekranda)
function CallHead() {
  return (
    <div className="op2-call head">
      <span />
      <span>Mijoz</span>
      <span>Telefon raqami</span>
      <span>Vaqt</span>
      <span>Natija</span>
    </div>
  );
}

export default function WorkspacePage() {
  const p = usePhoneCore();
  const outlet = useOutletContext();
  // Kontakt saqlash oynasi layout'da (OrgBranchPicker bilan) — shu yerdan chaqiriladi.
  const onAddContact = useCallback((phone) => {
    if (outlet && outlet.openSaveContact) outlet.openSaveContact(phone);
  }, [outlet]);

  const [dashboard, setDashboard] = useState(null);
  const [calls, setCalls] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('callback');  // callback | tasks | calls
  const [period, setPeriod] = useState('7');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [noteCall, setNoteCall] = useState(null);
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    dispositionsAPI.codes().then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, []);

  // Har bosilgan harfda so'rov yubormaymiz.
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const loadData = useCallback(async () => {
    // QIDIRUVDA davr chegarasi olib tashlanadi: "qachondir gaplashilgan"
    // raqamni butun tarix bo'ylab topish kerak.
    const range = query ? {} : rangeFor(period);
    const [dashRes, callsRes, tasksRes] = await Promise.all([
      dashboardAPI.get().catch(() => null),
      callsAPI.list({ limit: 200, ...range, ...(query ? { search: query } : {}) }).catch(() => null),
      tasksAPI.list({ status: 'open' }).catch(() => null),
    ]);
    if (dashRes) {
      setDashboard((prev) => (prev && JSON.stringify(prev) === JSON.stringify(dashRes.data) ? prev : dashRes.data));
    }
    if (callsRes) setCalls((prev) => reuseRows(prev, callsRes.data.calls || []));
    if (tasksRes) setTasks((prev) => reuseRows(prev, tasksRes.data.tasks || []));
    setLoading(false);
  }, [period, query]);

  // Har 15 soniyada yangilash — lekin panel ko'rinmay turganda (boshqa oyna
  // yoki yig'ilgan brauzer) so'rov yubormaymiz; qaytib kelinganda darhol yangilanadi.
  useEffect(() => {
    loadData();
    const id = setInterval(() => { if (!document.hidden) loadData(); }, 15000);
    const onVis = () => { if (!document.hidden) loadData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadData]);

  // Bo'lim, davr yoki qidiruv o'zgarsa — yana birinchi 50 ta qatordan boshlaymiz.
  useEffect(() => { setShown(PAGE); }, [tab, period, query]);

  // Izoh saqlanganda jurnal darhol yangilansin (15 s kutmasdan).
  useEffect(() => {
    const onSaved = () => loadData();
    window.addEventListener('cc:call-note-saved', onSaved);
    return () => window.removeEventListener('cc:call-note-saved', onSaved);
  }, [loadData]);

  const sorted = useMemo(
    () => [...calls].sort((a, b) => new Date(b.call_date) - new Date(a.call_date)),
    [calls],
  );

  // "Qayta qo'ng'iroq kerak": har raqamning ENG OXIRGI qo'ng'irog'i javobsiz
  // bo'lsa — ro'yxatga tushadi. Keyin javob berilgan bo'lsa chiqib ketadi,
  // ya'ni ro'yxat o'zi tozalanadi va bir raqam ikki marta ko'rinmaydi.
  const callbacks = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of sorted) {
      const num = callNumber(c);
      if (!num || isInternalNumber(num) || seen.has(num)) continue;
      seen.add(num);
      if (c.status !== 'answered') out.push(c);
    }
    return out;
  }, [sorted]);

  const isOverdue = (t) => t.due_at && new Date(t.due_at) < new Date();
  const openTasks = useMemo(
    () => [...tasks].sort((a, b) => (isOverdue(b) ? 1 : 0) - (isOverdue(a) ? 1 : 0)),
    [tasks],
  );
  const overdueCount = tasks.filter(isOverdue).length;

  const dial = p.dial;

  const completeTask = async (id) => {
    try { await tasksAPI.complete(id); loadData(); } catch (e) { /* jim */ }
  };

  const t = dashboard?.today || {};

  const TABS = [
    { key: 'callback', label: "Qayta qo'ng'iroq", icon: FiRotateCcw, count: callbacks.length },
    { key: 'tasks', label: 'Vazifalar', icon: FiCheckSquare, count: openTasks.length },
    { key: 'calls', label: 'Barcha qo\'ng\'iroqlar', icon: FiList, count: sorted.length },
  ];

  // Barqaror obyekt — memo qilingan qatorlar keraksiz qayta chizilmasin.
  const rowProps = useMemo(() => ({
    codes, onDial: p.dial, onNote: setNoteCall, onAddContact,
    onChanged: loadData, onOpenCard: p.openCard,
  }), [codes, p.dial, onAddContact, loadData, p.openCard]);

  // "Yana ko'rsatish" tugmasi
  const more = (total) => (total > shown ? (
    <div style={{ padding: '12px 16px', borderTop: '1px solid var(--g-br-2)', textAlign: 'center' }}>
      <button className="op2-btn" onClick={() => setShown((n) => n + PAGE)}>
        Yana {Math.min(PAGE, total - shown)} ta ko'rsatish · jami {total}
      </button>
    </div>
  ) : null);

  return (
    <>
      {/* Mijoz kartasi — jurnalda ismni bosganda ochiladi, o'zi ochilmaydi */}
      <PeerCard onSaveContact={onAddContact} />

      <div className="op2-kpis">
        <Kpi label="Bugun" value={t.total || 0} sub="qo'ng'iroq" />
        <Kpi label="Javob berildi" value={t.answered || 0} color="var(--g-ok)"
          sub={t.answer_rate != null ? `${t.answer_rate}% javob darajasi` : null} />
        <Kpi label="Javobsiz" value={t.missed || 0} color={t.missed ? 'var(--g-bad)' : undefined} sub="bugun" />
        <Kpi label="O'rtacha" value={formatDuration(t.avg_duration || 0)} sub="suhbat davomiyligi" />
      </div>

      <div className="op2-panel g">
        <div className="op2-panel-head">
          <div className="op2-pills">
            {TABS.map((x) => (
              <button key={x.key} className={tab === x.key ? 'on' : ''} onClick={() => setTab(x.key)}>
                <x.icon size={14} />
                {x.label}
                <span className="n">{x.count}</span>
              </button>
            ))}
          </div>

          {tab === 'tasks' && overdueCount > 0 && (
            <span className="op2-tag bad"><FiAlertCircle size={11} /> {overdueCount} kechikkan</span>
          )}

          {tab !== 'tasks' && (
            <>
              <div className="op2-search">
                <FiSearch size={15} />
                <input
                  className="op2-in"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Raqam yoki ism bo'yicha qidirish"
                />
                {search && (
                  <button className="op2-clear" onClick={() => setSearch('')} title="Tozalash">
                    <FiX size={14} />
                  </button>
                )}
              </div>
              {query ? (
                <span className="op2-tag ac">butun tarix bo'yicha</span>
              ) : (
                <div className="op2-pills">
                  {PERIODS.map((x) => (
                    <button
                      key={x.key}
                      className={period === x.key ? 'on' : ''}
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      onClick={() => setPeriod(x.key)}
                    >
                      {x.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {loading ? (
          <div className="op2-empty">Yuklanmoqda…</div>
        ) : tab === 'tasks' ? (
          openTasks.length === 0 ? (
            <div className="op2-empty">Ochiq vazifa yo'q 🎉</div>
          ) : (
            <div>
              {openTasks.map((tk) => {
                const num = taskNumber(tk);
                return (
                  <div key={tk.id} className="op2-li">
                    <span
                      style={{
                        width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                        background: tk.priority === 'high' ? 'var(--g-bad)'
                          : tk.priority === 'low' ? 'var(--g-tx3)' : 'var(--g-ac)',
                      }}
                    />
                    <div className="op2-li-m">
                      <div className="op2-li-t">
                        <span className="op2-li-n">{tk.title}</span>
                        {!tk.assigned_to && <span className="op2-tag warn">Umumiy</span>}
                      </div>
                      {tk.description && (
                        <div className="op2-li-note" style={{ whiteSpace: 'pre-wrap' }}>{tk.description}</div>
                      )}
                      <div className="op2-li-s">
                        {(tk.contact_name || tk.contact_phone) && (
                          <span><FiUser size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{tk.contact_name || tk.contact_phone}</span>
                        )}
                        {tk.due_at && (
                          <span style={{ color: isOverdue(tk) ? 'var(--g-bad)' : undefined, fontWeight: isOverdue(tk) ? 700 : 400 }}>
                            <FiClock size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
                            {formatDateTime(tk.due_at)}{isOverdue(tk) ? ' · kechikkan' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="op2-li-a">
                      {num && (
                        <button className="op2-sm go" onClick={() => dial(num, tk.contact_name)} title="Qo'ng'iroq qilish">
                          <FiPhoneCall size={15} />
                        </button>
                      )}
                      <button className="op2-sm" title="Bajarildi" onClick={() => completeTask(tk.id)}>
                        <FiCheck size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--g-br-2)' }}>
                <Link to="tasks" style={{ fontSize: 13, fontWeight: 700, color: 'var(--g-ac)' }}>
                  Vazifalar sahifasi →
                </Link>
              </div>
            </div>
          )
        ) : tab === 'callback' ? (
          callbacks.length === 0 ? (
            <div className="op2-empty">
              {query ? `«${query}» bo'yicha javobsiz qo'ng'iroq yo'q` : "Javobsiz qolgan qo'ng'iroq yo'q 👍"}
            </div>
          ) : (
            <div>
              <div className="op2-hint">
                Oxirgi qo'ng'irog'i javobsiz qolgan raqamlar. Qayta gaplashilgandan keyin ro'yxatdan o'zi chiqadi.
              </div>
              <CallHead />
              {callbacks.slice(0, shown).map((c) => <CallRow key={c.id} call={c} {...rowProps} />)}
              {more(callbacks.length)}
            </div>
          )
        ) : (
          sorted.length === 0 ? (
            <div className="op2-empty">
              {query ? `«${query}» bo'yicha hech narsa topilmadi` : "Bu davrda qo'ng'iroq yo'q"}
            </div>
          ) : (
            <div>
              <CallHead />
              {sorted.slice(0, shown).map((c) => <CallRow key={c.id} call={c} {...rowProps} />)}
              {more(sorted.length)}
            </div>
          )
        )}
      </div>

      {noteCall && (
        <CallNoteModal call={noteCall} onClose={() => setNoteCall(null)} onSave={() => loadData()} />
      )}
    </>
  );
}
